import React, { useState, useEffect, useCallback } from 'react';
import { useToast } from '../../../components/Toast';
import './HighlightsForm.css';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

// Helper to normalize date to YYYY-MM-DD format
function normalizeDate(dateValue) {
  if (!dateValue) return '';
  
  try {
    // If it's already in YYYY-MM-DD format, return as-is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      return dateValue;
    }
    
    // Try parsing as a date string
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) {
      return '';
    }
    
    // Return in ISO format (YYYY-MM-DD)
    return date.toISOString().split('T')[0];
  } catch (e) {
    return '';
  }
}

export default function NewsForm({ apiBase }) {
  const toastUtil = useToast();
  const ToastContainer = toastUtil.ToastContainer;
  const toast = toastUtil;

  const [sectionId, setSectionId] = useState(37);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState({});
  const [imageUploading, setImageUploading] = useState({});
  const [imageProgress, setImageProgress] = useState({});
  const [editingItemIndex, setEditingItemIndex] = useState(null);
  const [editingMode, setEditingMode] = useState(null);
  const [tempNewItem, setTempNewItem] = useState(null);

  const [mediaMap, setMediaMap] = useState({});

  const fetchMediaMap = useCallback(async () => {
    try {
      const response = await fetch(
        `${apiBase}/homepage/cms/media?type=image&_t=${Date.now()}`,
        { 
          credentials: 'include',
          headers: {
            'Cache-Control': 'no-cache',
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) return;

      const result = await response.json();
      if (result.ok && Array.isArray(result.data)) {
        const map = {};
        result.data.forEach(media => {
          const mediaId = media.id || media.media_id;
          const mediaUrl = media.url || media.file_path;
          if (mediaId && mediaUrl) map[mediaId] = mediaUrl;
        });
        setMediaMap(map);
      }
    } catch (err) {
      console.warn('Media fetch error:', err.message);
    }
  }, [apiBase]);

  const getImageUrl = (mediaId) => {
    if (!mediaId) return null;
    if (mediaMap[mediaId]) return mediaMap[mediaId];
    
    const mediaStr = String(mediaId);
    if (mediaStr.startsWith('http')) return mediaStr;
    if (mediaStr.startsWith('/')) return mediaStr;
    if (mediaStr.includes('.')) return `/api/uploads/images/${mediaStr}`;
    return null;
  };

  const [formData, setFormData] = useState({
    kicker: '',
    title: '',
    intro: '',
    latest_news_label: '',
    items: []
  });

  const [pendingFields, setPendingFields] = useState({
    kicker: false,
    title: false,
    intro: false,
    latest_news_label: false
  });

  const [originalData, setOriginalData] = useState({...formData});
  const [itemOperations, setItemOperations] = useState({});

  const loadNewsSection = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${apiBase}/cms/section/37/with-pending`,
        { 
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        }
      );

      if (!response.ok) throw new Error(`API returned ${response.status}`);

      const result = await response.json();

      if (result.success && result.data) {
        const { section, pending_changes } = result.data;
        
        if (section && section.content_published) {
          let published = section.content_published;
          if (typeof published === 'string') {
            try {
              published = JSON.parse(published);
            } catch (e) {
              published = {};
            }
          }

          const publishedEn = published.en || {};
          
          // Normalize dates in items to ensure YYYY-MM-DD format
          const normalizedItems = (publishedEn.items || []).map(item => ({
            ...item,
            date: item.date ? normalizeDate(item.date) : ''
          }));
          
          const initialData = {
            kicker: publishedEn.kicker || '',
            title: publishedEn.title || '',
            intro: publishedEn.intro || '',
            latest_news_label: publishedEn.latest_news_label || '',
            items: normalizedItems
          };

          setFormData(initialData);
          setOriginalData(initialData);
          setSectionId(section.id);

          if (Array.isArray(pending_changes)) {
            const pendingMap = {
              kicker: false,
              title: false,
              intro: false,
              latest_news_label: false
            };

            const itemOpsMap = {};

            pending_changes.forEach(change => {
              if (change.status === 'pending' && change.locale === 'en') {
                if (change.field_id in pendingMap) {
                  pendingMap[change.field_id] = true;
                }
                if (change.field_id && change.field_id.includes('items.')) {
                  const match = change.field_id.match(/items\.(\d+)\.(add|update|delete)/);
                  if (match) {
                    const itemIdx = parseInt(match[1]);
                    itemOpsMap[itemIdx] = { operation: match[2], status: 'pending' };
                  }
                }
              }
            });

            setPendingFields(pendingMap);
            setItemOperations(itemOpsMap);
          }
        }
      }
    } catch (err) {
      console.error('Error loading news section:', err.message);
    } finally {
      setLoading(false);
    }
  }, [apiBase]);

  useEffect(() => {
    loadNewsSection();
    fetchMediaMap();
  }, [loadNewsSection, fetchMediaMap]);

  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleItemChange = (field, value) => {
    console.log('[NewsForm] handleItemChange:', { field, value, editingMode, editingItemIndex });
    
    if (editingMode === 'add') {
      console.log('[NewsForm] Updating tempNewItem');
      setTempNewItem(prev => ({
        ...prev,
        [field]: value
      }));
    } else {
      const actualIndex = editingItemIndex;
      console.log('[NewsForm] Updating item at index:', actualIndex);
      setFormData(prev => ({
        ...prev,
        items: prev.items.map((item, idx) =>
          idx === actualIndex ? { ...item, [field]: value } : item
        )
      }));

      const originalItem = originalData.items?.[actualIndex];
      if (originalItem && !itemOperations[actualIndex]) {
        setItemOperations(prev => ({
          ...prev,
          [actualIndex]: { operation: 'update', status: 'pending_edit' }
        }));
      }
    }
  };

  const handleAddItem = () => {
    const newItem = {
      id: `item_new_${Date.now()}`,
      date: '',
      title: '',
      summary: '',
      link: '',
      media_id: null
    };
    
    setTempNewItem(newItem);
    setEditingMode('add');
    setEditingItemIndex(null);
  };

  const handleDeleteItem = (index) => {
    const item = formData.items[index];
    const itemId = item.id || `item_${index}_${Date.now()}`;
    
    if (window.confirm('Delete this news item? (Will need approval)')) {
      setItemOperations(prev => ({
        ...prev,
        [index]: { operation: 'delete', status: 'pending_submission' }
      }));
      
      handleSubmitItemOperation(index, 'delete', itemId);
      toast.info('Item marked for deletion (pending approval)');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_IMAGE_SIZE) {
      toast.error(`Image max 5MB, your file is ${(file.size / 1024 / 1024).toFixed(2)}MB`);
      return;
    }

    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Only JPG, PNG or WebP allowed');
      return;
    }

    const itemIndex = editingMode === 'add' ? 'add' : editingItemIndex;
    setImageUploading(prev => ({ ...prev, [itemIndex]: true }));
    setImageProgress(prev => ({ ...prev, [itemIndex]: 0 }));

    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percent = Math.round((e.loaded / e.total) * 100);
          setImageProgress(prev => ({ ...prev, [itemIndex]: percent }));
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 200) {
          const response = JSON.parse(xhr.responseText);
          if (response.success) {
            handleItemChange('media_id', response.filename);
            toast.success('Image uploaded');
          } else {
            toast.error(response.error || 'Upload failed');
          }
        } else {
          toast.error('Upload failed');
        }
        setImageUploading(prev => ({ ...prev, [itemIndex]: false }));
        setImageProgress(prev => ({ ...prev, [itemIndex]: 0 }));
      });

      xhr.addEventListener('error', () => {
        toast.error('Upload error');
        setImageUploading(prev => ({ ...prev, [itemIndex]: false }));
        setImageProgress(prev => ({ ...prev, [itemIndex]: 0 }));
      });

      xhr.open('POST', `${apiBase}/media/upload`);
      xhr.withCredentials = true;
      xhr.send(uploadFormData);
    } catch (err) {
      toast.error('Upload error: ' + err.message);
      const itemIndex = editingMode === 'add' ? 'add' : editingItemIndex;
      setImageUploading(prev => ({ ...prev, [itemIndex]: false }));
      setImageProgress(prev => ({ ...prev, [itemIndex]: 0 }));
    }
  };

  const handleSubmitField = async (fieldName) => {
    if (!sectionId) {
      toast.error('Section ID not loaded');
      return;
    }

    if (formData[fieldName] === originalData[fieldName]) {
      toast.warning('No changes to submit');
      return;
    }

    setSubmitting(prev => ({ ...prev, [fieldName]: true }));

    try {
      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionId,
            field_id: fieldName,
            field_name: fieldName.replace(/_/g, ' '),
            old_value: originalData[fieldName],
            new_value: formData[fieldName],
            locale: 'en'
          })
        }
      );

      const result = await response.json();

      if (result.success) {
        toast.success(`Submitted for approval`);
        
        setPendingFields(prev => ({
          ...prev,
          [fieldName]: true
        }));

        setOriginalData(prev => ({
          ...prev,
          [fieldName]: formData[fieldName]
        }));
      } else {
        if (response.status === 409) {
          toast.error('This field already has a pending approval');
        } else {
          toast.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [fieldName]: false }));
    }
  };

  const handleSubmitItemOperation = async (index, operation, itemId) => {
    if (!sectionId) {
      toast.error('Section ID not loaded');
      return;
    }

    const item = editingMode === 'add' ? tempNewItem : formData.items[index];
    
    if (!item && operation !== 'delete') {
      toast.error('Item not found');
      return;
    }

    setSubmitting(prev => ({ ...prev, [`item_${index}_${operation}`]: true }));

    try {
      let fieldId, fieldName, oldValue, newValue;

      if (operation === 'delete') {
        fieldId = `items.${index}.delete`;
        fieldName = `Delete Item: ${item?.title || 'Untitled'}`;
        oldValue = item || null;
        newValue = null;
      } else if (operation === 'add') {
        fieldId = `items.${index}.add`;
        fieldName = `Add Item: ${item.title || 'New Item'}`;
        oldValue = null;
        newValue = item;
      } else if (operation === 'update') {
        fieldId = `items.${index}.update`;
        fieldName = `Update Item: ${item.title || 'Untitled'}`;
        oldValue = originalData.items?.[index] || null;
        newValue = item;
      }

      console.log('[NewsForm] Submitting:', { fieldId, operation, index, oldValue, newValue });

      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionId,
            field_id: fieldId,
            field_name: fieldName,
            old_value: oldValue,
            new_value: newValue,
            locale: 'en',
            item_id: null,
            operation_type: operation
          })
        }
      );

      const result = await response.json();

      if (result.success) {
        if (operation === 'add') {
          const newIndex = formData.items.length;
          setFormData(prev => ({
            ...prev,
            items: [...prev.items, tempNewItem]
          }));
          setItemOperations(prev => ({
            ...prev,
            [newIndex]: { operation: 'add', status: 'pending' }
          }));
          setTempNewItem(null);
          setEditingMode(null);
          setEditingItemIndex(null);
        } else {
          setItemOperations(prev => ({
            ...prev,
            [index]: { operation, status: 'pending' }
          }));
        }

        toast.success(`Item ${operation} submitted for approval`);
      } else {
        if (response.status === 409) {
          toast.error(`Item ${operation} already has a pending approval`);
        } else {
          toast.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [`item_${index}_${operation}`]: false }));
    }
  };

  if (loading) {
    return (
      <div className="hero-form-wrapper">
        <div style={{padding: '40px', textAlign: 'center'}}>Loading...</div>
      </div>
    );
  }

  return (
    <>
      <div className="hero-form-wrapper">
        <ToastContainer />

        <div className="hero-form-header">
          <h2 className="hero-form-title">News & Updates Section</h2>
          <p className="hero-form-description">Edit header and manage news items (all changes require approval)</p>

          <div className="form-section">
            <h3 className="form-section-title">Header Content</h3>

            <div className="form-field-card">
              <div className="field-header">
                <label>Kicker / Subtitle</label>
                {pendingFields.kicker && (
                  <span className="status-badge pending">Pending</span>
                )}
              </div>
              <input
                type="text"
                maxLength="50"
                value={formData.kicker}
                onChange={(e) => handleFieldChange('kicker', e.target.value)}
                disabled={submitting.kicker || pendingFields.kicker}
                placeholder="e.g., What's Next"
                className="text-input"
              />
              <button
                onClick={() => handleSubmitField('kicker')}
                disabled={submitting.kicker || pendingFields.kicker || formData.kicker === originalData.kicker}
                className="submit-btn"
              >
                {submitting.kicker ? 'Submitting...' : 'Submit'}
              </button>
            </div>

            <div className="form-field-card">
              <div className="field-header">
                <label>Section Title</label>
                {pendingFields.title && (
                  <span className="status-badge pending">Pending</span>
                )}
              </div>
              <input
                type="text"
                maxLength="100"
                value={formData.title}
                onChange={(e) => handleFieldChange('title', e.target.value)}
                disabled={submitting.title || pendingFields.title}
                placeholder="e.g., News and Updates"
                className="text-input"
              />
              <button
                onClick={() => handleSubmitField('title')}
                disabled={submitting.title || pendingFields.title || formData.title === originalData.title}
                className="submit-btn"
              >
                {submitting.title ? 'Submitting...' : 'Submit'}
              </button>
            </div>

            <div className="form-field-card">
              <div className="field-header">
                <label>Introduction</label>
                {pendingFields.intro && (
                  <span className="status-badge pending">Pending</span>
                )}
              </div>
              <textarea
                maxLength="500"
                value={formData.intro}
                onChange={(e) => handleFieldChange('intro', e.target.value)}
                disabled={submitting.intro || pendingFields.intro}
                placeholder="Section introduction..."
                className="textarea-input"
                rows="3"
              />
              <button
                onClick={() => handleSubmitField('intro')}
                disabled={submitting.intro || pendingFields.intro || formData.intro === originalData.intro}
                className="submit-btn"
              >
                {submitting.intro ? 'Submitting...' : 'Submit'}
              </button>
            </div>

            <div className="form-field-card">
              <div className="field-header">
                <label>Latest News Label</label>
                {pendingFields.latest_news_label && (
                  <span className="status-badge pending">Pending</span>
                )}
              </div>
              <input
                type="text"
                maxLength="50"
                value={formData.latest_news_label}
                onChange={(e) => handleFieldChange('latest_news_label', e.target.value)}
                disabled={submitting.latest_news_label || pendingFields.latest_news_label}
                placeholder="e.g., Latest News"
                className="text-input"
              />
              <button
                onClick={() => handleSubmitField('latest_news_label')}
                disabled={submitting.latest_news_label || pendingFields.latest_news_label || formData.latest_news_label === originalData.latest_news_label}
                className="submit-btn"
              >
                {submitting.latest_news_label ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </div>

          <div className="form-section">
            <div className="items-section-header">
              <h3 className="form-section-title">News Items</h3>
              <button className="btn-add-item" onClick={handleAddItem}>
                + Add Item
              </button>
            </div>

            {formData.items.length === 0 ? (
              <div className="items-empty-state">
                <div className="empty-icon">No items</div>
                <h4>No items yet</h4>
                <p>Create your first news item to get started</p>
              </div>
            ) : (
              <div className="items-list">
                {formData.items.map((item, index) => (
                  <div key={index} className={`items-list-item ${itemOperations[index]?.status === 'pending' ? 'item-pending' : ''}`}>
                    <div className="items-list-image">
                      {item.media_id ? (
                        <img src={getImageUrl(item.media_id)} alt={item.title} onError={(e) => {e.target.src = ''; e.target.style.display = 'none'; e.target.nextElementSibling.style.display = 'flex';}} />
                      ) : null}
                      {!item.media_id ? (
                        <div className="items-list-no-image">No Image</div>
                      ) : null}
                    </div>
                    <div className="items-list-content">
                      <h4 className="items-list-title">{item.title || 'Untitled'}</h4>
                      <p className="items-list-summary">{item.summary || 'No description'}</p>
                    </div>
                    
                  <div className="items-list-actions">
                    {itemOperations[index]?.status === 'pending' && (
                      <span className="items-list-status">Pending</span>
                    )}
                    <button
                      onClick={() => {
                        setEditingMode('edit');
                        setEditingItemIndex(index);
                      }}
                      disabled={itemOperations[index]?.status === 'pending'}
                      className="items-list-btn items-list-btn-edit"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteItem(index)}
                      disabled={itemOperations[index]?.status === 'pending' || itemOperations[index]?.operation === 'delete'}
                      className="items-list-btn items-list-btn-delete"
                    >
                      Delete
                    </button>
                  </div>
                </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {(editingItemIndex !== null || editingMode === 'add') && (editingMode === 'add' ? tempNewItem : formData.items[editingItemIndex]) && (
        <div className="edit-modal-overlay" onClick={() => {
          setEditingMode(null);
          setEditingItemIndex(null);
          setTempNewItem(null);
        }}>
          <div className="edit-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-new">
              <h3>{editingMode === 'add' ? 'Add New Item' : `Edit: ${(editingMode === 'add' ? tempNewItem : formData.items[editingItemIndex])?.title || 'Item'}`}</h3>
              <button className="modal-close-btn" onClick={() => {
                setEditingMode(null);
                setEditingItemIndex(null);
                setTempNewItem(null);
              }}>×</button>
            </div>

            <div className="modal-body">
              <div className="modal-image-section">
                <div className="modal-image-preview">
                  {(editingMode === 'add' ? tempNewItem : formData.items[editingItemIndex])?.media_id ? (
                    <img src={getImageUrl((editingMode === 'add' ? tempNewItem : formData.items[editingItemIndex]).media_id)} alt={(editingMode === 'add' ? tempNewItem : formData.items[editingItemIndex])?.title} onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextElementSibling.style.display = 'flex';
                    }} />
                  ) : null}
                  <div className="modal-image-placeholder" style={{display: (editingMode === 'add' ? tempNewItem : formData.items[editingItemIndex])?.media_id ? 'none' : 'flex'}}>
                    <span>No image yet</span>
                  </div>
                </div>
              </div>

              <div className="modal-field-group">
                <label>Date</label>
                <input
                  type="date"
                  value={(editingMode === 'add' ? tempNewItem : formData.items[editingItemIndex])?.date || ''}
                  onChange={(e) => handleItemChange('date', e.target.value)}
                  className="modal-input"
                  min="2000-01-01"
                  max="2099-12-31"
                />
              </div>

              <div className="modal-field-group">
                <label>Title</label>
                <input
                  type="text"
                  value={(editingMode === 'add' ? tempNewItem : formData.items[editingItemIndex])?.title || ''}
                  onChange={(e) => handleItemChange('title', e.target.value)}
                  placeholder="News title"
                  className="modal-input"
                  maxLength="150"
                />
              </div>

              <div className="modal-field-group">
                <label>Summary</label>
                <textarea
                  value={(editingMode === 'add' ? tempNewItem : formData.items[editingItemIndex])?.summary || ''}
                  onChange={(e) => handleItemChange('summary', e.target.value)}
                  placeholder="News description"
                  className="modal-textarea"
                  maxLength="500"
                  rows="3"
                />
              </div>

              <div className="modal-field-group">
                <label>Link (optional)</label>
                <input
                  type="text"
                  value={(editingMode === 'add' ? tempNewItem : formData.items[editingItemIndex])?.link || ''}
                  onChange={(e) => handleItemChange('link', e.target.value)}
                  placeholder="Optional URL to full article"
                  className="modal-input"
                  maxLength="300"
                />
              </div>

              <div className="modal-field-group">
                <label>Image</label>
                <div className="modal-upload-area">
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleImageUpload}
                    disabled={imageUploading[editingMode === 'add' ? 'add' : editingItemIndex]}
                    id={`modal-image-${editingItemIndex}`}
                    className="modal-file-input"
                  />
                  <label htmlFor={`modal-image-${editingItemIndex}`} className="modal-upload-label">
                    <div className="upload-icon-new">Image</div>
                    <span>{imageUploading[editingMode === 'add' ? 'add' : editingItemIndex] ? `${imageProgress[editingMode === 'add' ? 'add' : editingItemIndex]}%` : 'Choose Image'}</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="modal-btn-cancel" onClick={() => {
                setEditingMode(null);
                setEditingItemIndex(null);
                setTempNewItem(null);
              }}>
                Cancel
              </button>
              <button
                onClick={() => {
                  const operation = editingMode === 'add' ? 'add' : 'update';
                  const index = editingMode === 'add' ? formData.items.length : editingItemIndex;
                  handleSubmitItemOperation(index, operation, '');
                }}
                disabled={submitting[`item_${editingMode === 'add' ? 'add' : editingItemIndex}_${editingMode === 'add' ? 'add' : 'update'}`]}
                className="modal-btn-submit"
              >
                {editingMode === 'add' ? 'Save Item' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
